import { useEffect, useState, useRef, useCallback } from "react";
import { Send, Paperclip, Loader2, Download, X } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DictateButton } from "@/components/DictateButton";

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
};

const isImage = (ct) => (ct || "").startsWith("image/");

/**
 * MessageThread — reusable chat dialog (text + attachment) for project- or
 * user-scoped threads.
 * Props:
 *   - scopeType: "project" | "user"
 *   - scopeId:   string (project_id or user_id)
 *   - title:     optional heading
 *   - onActivity: callback fired after send/load so parent can refresh unread badge
 */
export function MessageThread({ scopeType, scopeId, title, onActivity }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    if (!scopeId) return;
    try {
      const r = await api.get("/messages", {
        params: { scope_type: scopeType, scope_id: scopeId },
      });
      setMessages(r.data);
      onActivity?.();
    } catch (e) {
      toast.error("Nachrichten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [scopeType, scopeId, onActivity]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    // Scroll to newest after load/send
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const send = async () => {
    if (!text.trim() && !file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("scope_type", scopeType);
      fd.append("scope_id", scopeId);
      fd.append("body", text.trim());
      if (file) fd.append("attachment", file, file.name || "anhang");
      await api.post("/messages", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senden fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 8 MB)");
      return;
    }
    setFile(f);
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden flex flex-col" data-testid={`message-thread-${scopeType}-${scopeId}`}>
      {title && (
        <div className="px-4 py-3 border-b border-zinc-800">
          <h3 className="font-display text-base font-semibold">{title}</h3>
        </div>
      )}

      {/* Messages list */}
      <div className="px-3 py-3 space-y-2 max-h-[60vh] overflow-y-auto">
        {loading ? (
          <div className="text-center py-8 text-zinc-500">
            <Loader2 className="animate-spin inline mr-2" /> Lade…
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-zinc-400 text-sm font-medium">
            Noch keine Nachrichten. Schreib die erste!
          </div>
        ) : (
          messages.map((m) => {
            const isOwn = m.sender_id === user?.id;
            return (
              <div
                key={m.id}
                data-testid={`msg-${m.id}`}
                className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${
                    isOwn
                      ? "bg-[#00E575] text-[#09090B] rounded-br-md"
                      : "bg-[#27272A] text-zinc-100 rounded-bl-md"
                  }`}
                >
                  {!isOwn && (
                    <div className="text-[12px] font-bold uppercase tracking-wider opacity-70 mb-1">
                      {m.sender_name}
                    </div>
                  )}
                  {m.body && (
                    <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                  )}
                  {m.attachment?.path && (
                    <a
                      href={fileUrl(m.attachment.path)}
                      target="_blank"
                      rel="noreferrer"
                      className={`mt-2 inline-flex items-center gap-1.5 text-xs underline ${isOwn ? "text-[#09090B]/80" : "text-[#00E575]"}`}
                    >
                      {isImage(m.attachment.content_type) ? (
                        <img
                          src={fileUrl(m.attachment.path)}
                          alt={m.attachment.filename || "Anhang"}
                          className="rounded-lg max-h-48 mt-1"
                        />
                      ) : (
                        <>
                          <Download size={12} />
                          {m.attachment.filename || "Anhang"}
                        </>
                      )}
                    </a>
                  )}
                  <div className={`text-[12px] mt-1 ${isOwn ? "text-[#09090B]/60" : "text-zinc-300"}`}>
                    {fmtTime(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="border-t border-zinc-800 p-3 bg-[#0E0E12]">
        {file && (
          <div className="mb-2 flex items-center justify-between bg-zinc-800 rounded-lg px-2.5 py-1.5 text-xs">
            <span className="text-zinc-300 truncate">{file.name}</span>
            <button
              onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
              className="text-zinc-500 hover:text-red-400 ml-2"
              data-testid="msg-file-clear"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="msg-attach-btn"
            className="h-11 w-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-300 flex-shrink-0"
            aria-label="Datei anhängen"
          >
            <Paperclip size={18} />
          </button>
          <input
            ref={fileRef}
            type="file"
            onChange={handleFile}
            className="hidden"
            data-testid="msg-file-input"
          />
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !sending) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder="Nachricht schreiben…"
            data-testid="msg-input"
            className="flex-1 resize-none bg-[#18181B] border border-zinc-700 rounded-xl px-3 py-2.5 text-sm text-zinc-100 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none max-h-32"
          />
          <DictateButton
            testid="msg-dictate-btn"
            onAppend={(snippet) => {
              setText((prev) => (prev ? `${prev.trimEnd()} ${snippet}` : snippet));
            }}
          />
          <button
            onClick={send}
            disabled={sending || (!text.trim() && !file)}
            data-testid="msg-send-btn"
            className="h-11 w-11 rounded-xl bg-[#00E575] text-[#09090B] flex items-center justify-center disabled:opacity-40 hover:bg-[#00C964] flex-shrink-0 active:scale-95 transition"
            aria-label="Senden"
          >
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
